import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { X, Send, Headphones } from 'lucide-react';
import { playNotificationSound } from '../../utils/notificationSound';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SupportChat = ({ onClose, onUnreadChange }) => {
  const { getAuthHeader } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const prevCountRef = useRef(0);

  const loadMessages = async () => {
    try {
      const res = await axios.get(`${API}/admin/support-messages`, { headers: getAuthHeader() });
      const newMsgs = res.data;
      // Play sound if new superadmin message arrived
      const fromSA = newMsgs.filter(m => m.from_superadmin).length;
      if (fromSA > prevCountRef.current && prevCountRef.current > 0) {
        playNotificationSound('message');
      }
      prevCountRef.current = fromSA;
      setMessages(newMsgs);
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 4000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/support-messages`, { message: input.trim() }, { headers: getAuthHeader() });
      setInput('');
      loadMessages();
    } catch (err) {
      console.error(err);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end md:items-center justify-center z-50 md:p-4" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="bg-white rounded-t-xl md:rounded-xl w-full md:max-w-lg flex flex-col" style={{ height: '85vh', maxHeight: '85vh' }}>
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
              <Headphones size={20} className="text-accent-text" />
            </div>
            <div>
              <h3 className="font-bold">Soporte Vendix</h3>
              <p className="text-xs text-text-muted">Administracion de plataforma</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-support-chat">
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ background: '#f8f9fb' }}>
          {messages.length === 0 && (
            <div className="text-center text-text-muted text-sm py-8">
              Escribe un mensaje al administrador de Vendix
            </div>
          )}
          {messages.map((msg) => (
            <div key={msg.id} className={`flex ${msg.from_superadmin ? 'justify-start' : 'justify-end'}`}>
              <div
                className={`max-w-[75%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.from_superadmin
                    ? 'bg-white border text-gray-800'
                    : 'text-white'
                }`}
                style={msg.from_superadmin ? { borderColor: 'var(--border-light)' } : { background: 'var(--accent-text)' }}
              >
                <p>{msg.message}</p>
                <p className={`text-[10px] mt-1 ${msg.from_superadmin ? 'text-gray-400' : 'text-white/70'}`}>
                  {new Date(msg.timestamp).toLocaleString('es-EC', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' })}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>

        {/* Input */}
        <form onSubmit={handleSend} className="p-3 border-t flex gap-2" style={{ borderColor: 'var(--border-light)' }}>
          <input
            type="text"
            value={input}
            onChange={e => setInput(e.target.value)}
            placeholder="Escribe tu mensaje..."
            className="flex-1 px-4 py-2.5 rounded-xl border text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: 'var(--border-light)' }}
            data-testid="support-chat-input"
          />
          <button
            type="submit"
            disabled={sending || !input.trim()}
            className="px-4 py-2.5 rounded-xl text-white font-medium text-sm disabled:opacity-50"
            style={{ background: 'var(--accent-text)' }}
            data-testid="support-chat-send"
          >
            <Send size={18} />
          </button>
        </form>
      </div>
    </div>
  );
};

export default SupportChat;
